import { useEffect } from 'react';
import { allSettled } from 'effector';
import { Provider, useUnit } from 'effector-react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  CircularProgress,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  OutlinedInputProps,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import { Listbox } from '@features/effector-form/components/listbox';
import { withControl } from '@features/effector-form/hoc/withControl';
import {
  CustomControlProps,
  GeneralControlProps,
} from '@features/effector-form/types/control';
import { ErrorMessage } from './components/error';
import { $isLoading } from './models/loader';
import { $options, ControlGate, createScope, startRequest } from './models/model';
import * as Styled from './styled';
import { Option } from './types/main';

type AdditionalProps = {
  id: string;
  clearIcon?: boolean;
  multiple?: boolean;
};

export const PersonalAccountTypeListV4ControlBody = ({
  id,
  label,
  value,
  setValue,
  error,
  required,
  readOnly,
  disabled,
  clearIcon,
  multiple,
  placeholder,
}: GeneralControlProps & CustomControlProps<OutlinedInputProps & AdditionalProps>) => {
  const [options, isLoading] = useUnit([$options, $isLoading]);

  const scope = createScope(id);

  const handleStartRequest = async () => {
    await allSettled(startRequest, {
      scope,
    });
  };

  const handleChange = (_: any, newValue: any) => {
    if (multiple) {
      setValue(newValue.map((item: Option) => item.id) || null);
    } else {
      setValue(newValue?.id || null);
    }
  };

  const getValueByOptions = () => {
    if (value === null) return value;

    if (!options && multiple) return [];
    if (!options && !multiple) return null;

    return multiple
      ? options?.filter((item) => value?.indexOf(item.id) > -1)
      : (options?.find((item) => item.id === value) ?? null);
  };

  const resolveEndAdornment = (params: AutocompleteRenderInputParams) => {
    if (readOnly) return null;

    if (isLoading) return <CircularProgress size={20} color="inherit" />;

    return params.InputProps.endAdornment;
  };

  const resolveStartAdornment = (params: AutocompleteRenderInputParams) => {
    if (!readOnly) return params.InputProps.startAdornment;

    if (isLoading) return <CircularProgress size={20} color="inherit" />;

    return params.InputProps.startAdornment;
  };

  const setValueAfterOptionsChange = () => {
    const checkedValue = getValueByOptions();

    if (!checkedValue) {
      setValue(null);

      return;
    }
    setValue(
      Array.isArray(checkedValue) ? checkedValue.map(({ id }) => id) : checkedValue.id
    );
  };

  useEffect(() => {
    if (options === null) return;

    setValueAfterOptionsChange();
  }, [options]);

  useEffect(() => {
    handleStartRequest();
  }, []);

  const valueByOptions = getValueByOptions();
  const noValue =
    valueByOptions === null || valueByOptions === undefined || !valueByOptions.length;

  return (
    <>
      <ControlGate />

      <ErrorMessage id={id} />

      <Styled.CustomFormControl>
        {label && <FormLabel required={!readOnly && required}>{label}</FormLabel>}

        {readOnly && noValue && options !== null ? (
          <OutlinedInput
            value={valueByOptions?.label ?? '-'}
            readOnly={readOnly}
            disabled={disabled}
          />
        ) : (
          <Autocomplete
            loading={isLoading}
            value={valueByOptions}
            options={options ?? []}
            onChange={handleChange}
            ListboxComponent={Listbox}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={placeholder}
                InputProps={{
                  ...params.InputProps,
                  required,
                  readOnly,
                  disabled,
                  endAdornment: resolveEndAdornment(params),
                  startAdornment: resolveStartAdornment(params),
                }}
              />
            )}
            readOnly={readOnly}
            disabled={disabled}
            multiple={multiple}
            disableCloseOnSelect={multiple}
            clearIcon={clearIcon}
            size="small"
          />
        )}

        {error && <FormHelperText error>{error}</FormHelperText>}
      </Styled.CustomFormControl>
    </>
  );
};

export const PersonalAccountTypeListV4Control = withControl<
  OutlinedInputProps & AdditionalProps
>((props) => {
  const scope = createScope(props.id);

  return (
    <Provider value={scope}>
      <PersonalAccountTypeListV4ControlBody {...props} />
    </Provider>
  );
});
